<script lang="ts">
  import { createEventDispatcher, onMount } from 'svelte';
  import { ExtendedSizes, Target, Variant } from '@centerhealth/fe/center-kit';
  import { addDataAttrs, IHeapEvent } from '@centerhealth/fe/heap';
  import { AbTestService } from '@centerhealth/fe/abtest';
  import { mobileCtaFullWidthVariation } from '@centerhealth/fe/stores';

  export let variant: Variant = 'primary';
  export let size: ExtendedSizes = 'medium';

  export let href = '';

  export let block = false;
  // type: button or submit. NB: submit when button is used in a form
  export let type: 'button' | 'submit' = 'button';

  // Optional: hover buttons
  export let hover = '';

  export let customClass = '';

  export let customAnchorClass = '';

  export let target: Target = '';

  export let loading = false;

  export let disabled = false;

  export let heapAttr: IHeapEvent = null;

  export let mobileBlock = false;

  let activateMobileFullWidth: string;

  $: activateMobileFullWidth =
    $mobileCtaFullWidthVariation === 'withFullWidth' && mobileBlock ? 'mobile--block' : '';

  onMount(async () => {
    mobileCtaFullWidthVariation.set(
      await AbTestService.getVariation<'withFullWidth' | 'control'>('mobileBlock_22-08-03')
    );
  });

  const dispatch = createEventDispatcher();

  $: btnClasses = [
    'button',
    `button--${size}`,
    `button--${variant}`,
    `button--hover-${hover}`,
    customClass,
  ];

  /**
   * Optional click handler
   */
  function onClick(event: MouseEvent) {
    dispatch('click', event);
  }
</script>

{#if href}
  <a
    {href}
    {target}
    class:button--block={block === true}
    class={`${activateMobileFullWidth} ${customAnchorClass}`}
  >
    <button
      use:addDataAttrs={heapAttr}
      {type}
      disabled={loading === true || disabled}
      class={[...btnClasses, 'relative', activateMobileFullWidth].join(' ')}
      class:button--block={block === true}
      on:click={onClick}
    >
      {#if loading}
        <img width="30" height="30" class="loader-icon" src="/assets/loaders/light.svg" alt="" />
      {:else}
        <slot />
      {/if}
    </button>
  </a>
{:else}
  <button
    use:addDataAttrs={heapAttr}
    {type}
    disabled={loading === true || disabled}
    class={[...btnClasses, activateMobileFullWidth].join(' ')}
    class:button--block={block === true}
    on:click={onClick}
  >
    {#if loading}
      <img width="30" height="30" class="loader-icon" src="/assets/loaders/light.svg" alt="" />
    {:else}
      <slot />
    {/if}
  </button>
{/if}

<style lang="scss">
  @mixin animation {
    @apply transition-colors duration-200;
  }
  .button {
    font-family: Nunito;
    border-radius: 42px;
    border: none;
    font-weight: 600;
    @apply text-base;
    @apply border-solid;
    @apply border-2;
    @apply text-center;
    cursor: pointer;
  }
  .button--primary {
    color: white;
    @apply bg-blue;
    @apply border-blue;
    @include animation;
  }
  .button--primary:hover {
    @apply bg-blue-light;
    @apply border-blue-light;
    @include animation;
  }
  .button--secondary {
    @apply bg-transparent;
    @apply text-gray-dark;
    @apply border-gray-light;
    @include animation;
  }
  .button--hover-blue:hover {
    @apply lg:text-white lg:bg-blue lg:border-blue;
    @include animation;
  }
  .button--hover-green:hover {
    @apply text-white bg-green border-green;
    @include animation;
  }
  .button--hover-pink:hover {
    @apply text-white bg-pink border-pink;
    @include animation;
  }
  .button--tertiary {
    @apply bg-transparent;
    @apply text-white;
    @apply border-white;
    @include animation;
  }
  .button--light {
    @apply bg-white text-blue;
    @apply border-white;
    @include animation;
  }
  .button--white {
    @apply bg-white text-gray-darkest;
    @apply border-white;
    @include animation;
    box-shadow: 0px 3px 6px -2px rgba(194, 194, 194, 0.23);
  }
  .button--mini {
    font-size: 17px;
    padding: 3px 15px;
    border-radius: 16px;
  }
  .button--xsmall {
    font-size: 17px;
    padding: 3px 4px;
    border-radius: 16px;
  }
  .button--xxsmall {
    font-size: 17px;
    padding: 3px 4px;
    border-radius: 10px;
  }
  .button--small {
    padding: 9px 25px;
  }
  .button--medium {
    padding: 13px 25px;
  }
  .button--large {
    padding: 20px 15px;
  }
  .button--xlarge {
    @apply text-lg;
    padding: 16px 32px;

    @screen md {
      padding: 22px 75px;
    }
  }
  .button--block {
    @apply flex;
    @apply justify-center;
    width: 100%;
  }
  .loader-icon {
    height: 30px;
    width: 30px;
  }

  button:disabled {
    cursor: not-allowed;
  }

  .mobile--block {
    @media (max-width: 768px) {
      @apply flex justify-center w-full;
    }
  }

  .button--filled {
    @apply bg-smokewhite;
  }
</style>
